import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { lazy, Suspense, useState } from 'react';
import { Provider } from 'react-redux';
import store from './redux/store';
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ToastProvider } from "./context/ToastContext";
import { ConfirmProvider } from "./context/ConfirmContext";
import ErrorBoundary from './components/ErrorBoundary';

// Composants critiques — chargés immédiatement (requis pour l'auth et le layout)
import Login from './pages/Login';
import Register from './pages/Register';
import PrivateRoute from './utils/PrivateRoute';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';

// Pages principales — lazy loaded
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PortfolioDashboard = lazy(() => import('./pages/PortfolioDashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const Users = lazy(() => import('./pages/Users'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const CatalogEffects = lazy(() => import('./pages/CatalogEffects'));
const CatalogEffectYear = lazy(() => import('./pages/CatalogEffectYear'));
const Evaluation = lazy(() => import('./pages/evaluation/Evaluation'));
const EvaluationDetails = lazy(() => import('./pages/evaluation/EvaluationDetails'));
const EffectForm = lazy(() => import('./components/EffectForm'));

// Étapes du wizard — lazy loaded (composants lourds)
const Step0Structuration = lazy(() => import('./pages/evaluation/steps/Step0_Structuration'));
const Step1Options = lazy(() => import('./pages/evaluation/steps/Step1_Options'));
const Step2Actors = lazy(() => import('./pages/evaluation/steps/Step2_Actors'));
const Step3Effects = lazy(() => import('./pages/evaluation/steps/Step3_Effects'));
const Step4Quantification = lazy(() => import('./pages/evaluation/steps/Step4_Quantification'));
const Step5Indicators = lazy(() => import('./pages/evaluation/steps/Step5_Indicators'));
const Step6Risks = lazy(() => import('./pages/evaluation/steps/Step6_Risks'));
const Step7Report = lazy(() => import('./pages/evaluation/steps/Step7_Report'));
const ReportPage = lazy(() => import('./components/ReportPage'));
const ProjectNewPage  = lazy(() => import('./pages/ProjectFormPage').then(m => ({ default: m.ProjectNewPage })));
const ProjectEditPage = lazy(() => import('./pages/ProjectFormPage').then(m => ({ default: m.ProjectEditPage })));

// Nomenclatures — lazy loaded
const Sectors = lazy(() => import('./pages/nomenclatures/Sectors'));
const Ministries = lazy(() => import('./pages/nomenclatures/Ministries'));
const InterventionTypes = lazy(() => import('./pages/nomenclatures/InterventionTypes'));
const StructureTypes = lazy(() => import('./pages/nomenclatures/StructureTypes'));
const FundingTypes = lazy(() => import('./pages/nomenclatures/FundingTypes'));
const DocumentTypes = lazy(() => import('./pages/nomenclatures/DocumentTypes'));
const Regions = lazy(() => import('./pages/nomenclatures/Regions'));
const Departments = lazy(() => import('./pages/nomenclatures/Departments'));
const Communes = lazy(() => import('./pages/nomenclatures/Communes'));

// Rapport — lazy loaded
const ReportEditor = lazy(() => import('./components/ReportEditor'));
const ReportDownload = lazy(() => import('./components/ReportDownload'));
const NotFound = lazy(() => import('./pages/NotFound'));

function AppContent() {
  const token = useSelector((state) => state.auth.token);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <Router>
      {token ? (
        <div className="flex h-screen">
          <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
          <div className="flex flex-col w-full">
            <Navbar />
            <div
              className={`flex-1 p-6 overflow-y-auto transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-16'}`}
            >
              <DndProvider backend={HTML5Backend}>
                <Suspense fallback={<div className="flex items-center justify-center h-full"><p className="text-gray-500">Chargement...</p></div>}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                    <Route path="/portfolio" element={<PrivateRoute><PortfolioDashboard /></PrivateRoute>} />
                    <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
                    <Route path="/projects/new" element={<PrivateRoute><ProjectNewPage /></PrivateRoute>} />
                    <Route path="/projects/:id/edit" element={<PrivateRoute><ProjectEditPage /></PrivateRoute>} />
                    <Route path="/projects/:id" element={<PrivateRoute><ProjectDetails /></PrivateRoute>} />
                    <Route path="/evaluations" element={<PrivateRoute><Evaluation /></PrivateRoute>} />
                    <Route path="/evaluations/:id" element={<PrivateRoute><EvaluationDetails /></PrivateRoute>} />
                    <Route path="/CatalogEffects" element={<PrivateRoute><CatalogEffects /></PrivateRoute>} />
                    <Route path="/catalog-effects/:catalogEffectId/years" element={<PrivateRoute><CatalogEffectYear /></PrivateRoute>} />
                    <Route path="admin/users" element={<PrivateRoute><Users /></PrivateRoute>} />
                    {/* Routes du Wizard */}
                    <Route path="/evaluations/:id/step0" element={<PrivateRoute><Step0Structuration /></PrivateRoute>} />
                    <Route path="/evaluations/:id/step1" element={<PrivateRoute><Step1Options /></PrivateRoute>} />
                    <Route path="/evaluations/:id/step2" element={<PrivateRoute><Step2Actors /></PrivateRoute>} />
                    <Route path="/evaluations/:id/step3" element={<PrivateRoute><Step3Effects /></PrivateRoute>} />
                    <Route path="/evaluations/:id/step4" element={<PrivateRoute><ErrorBoundary><Step4Quantification /></ErrorBoundary></PrivateRoute>} />
                    <Route path="/evaluations/:id/step5" element={<PrivateRoute><Step5Indicators /></PrivateRoute>} />
                    <Route path="/evaluations/:id/step6" element={<PrivateRoute><ErrorBoundary><Step6Risks /></ErrorBoundary></PrivateRoute>} />
                    <Route path="/evaluations/:id/step7" element={<PrivateRoute><Step7Report /></PrivateRoute>} />
                    {/* Rapport */}
                    <Route path="/evaluation/:evaluationId/download" element={<PrivateRoute><ReportDownload /></PrivateRoute>} />
                    {/* Nomenclatures */}
                    <Route path="/nomenclatures/sectors" element={<Sectors />} />
                    <Route path="/nomenclatures/ministries" element={<Ministries />} />
                    <Route path="/nomenclatures/intervention-types" element={<InterventionTypes />} />
                    <Route path="/nomenclatures/structure-types" element={<StructureTypes />} />
                    <Route path="/nomenclatures/funding-types" element={<FundingTypes />} />
                    <Route path="/nomenclatures/document-types" element={<DocumentTypes />} />
                    {/* Localisation */}
                    <Route path="/nomenclatures/regions" element={<Regions />} />
                    <Route path="/nomenclatures/departments" element={<Departments />} />
                    <Route path="/nomenclatures/communes" element={<Communes />} />
                    {/* 404 */}
                    <Route path="*" element={<PrivateRoute><NotFound /></PrivateRoute>} />
                  </Routes>
                </Suspense>
              </DndProvider>
            </div>
          </div>
        </div>
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      )}
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ToastProvider>
        <ConfirmProvider>
          <AppContent />
        </ConfirmProvider>
      </ToastProvider>
    </Provider>
  );
}

export default App;
